import { useLang } from 'hooks/lang';

import { actions } from 'entities';

import { Column, Row, Modal, LabeledTextarea, LabeledInput, Button, Tag } from 'components/Blocks';
import { FromToArrows } from 'components/FromToArrows';
import { CircleXIcon, HandCoinsIcon, RefreshIcon } from 'components/Icons';

import { useToggleV2, useFormControl, useUser } from 'hooks';
import { useJustification } from 'providers/Justification';
import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';

import { defineIfCanChangeType } from './utils';

interface State {
  config: {
    comment: string;
    installments?: number;
  };
}

export function ChangePayment() {
  const user = useUser();
  const { t } = useLang();

  const { state, dispatch } = useFormControl<State>({
    config: {
      comment: '',
    },
  });

  const [open, modalHandlers] = useToggleV2();

  const { openJustificationModal } = useJustification();

  const {
    project: { invoice, id },
    updateProject,
  } = useProjectProfile();

  const switchMethod = invoice.type === 'PACKAGE' ? 'STANDARD' : 'PACKAGE';

  const changePaymentType = actions.invoice.changeProjectPaymentMethod.use({
    onSuccess: () => {
      updateProject((old) => ({
        ...old,

        invoice: {
          ...old.invoice,

          type: switchMethod,

          typeConfig: state.config,
        },
      }));

      modalHandlers.turnOff();
    },
  });

  const executeChoice = (): void => {
    openJustificationModal({
      title: t('change-method', { method: switchMethod }) as string,

      text: t('please-provide-a-detailed-explanation-change-method'),

      onConfirm: async (justification: string) => {
        const success = await changePaymentType.execute({
          project: id,

          justification,

          method: switchMethod,

          ...state,
        });

        if (!success) return;
      },
    });
  };

  if (!defineIfCanChangeType(invoice, user)) return null;

  return (
    <>
      <Modal
        title={t('change-payment-method') as string}
        width="min(80vw, 560px)"
        isOpen={open}
        close={modalHandlers.turnOff}
      >
        <Row justifyContent="center" gap="10" className="phone:flex-col phone:gap-4">
          <Tag
            name={t(invoice.type?.toLocaleLowerCase() as string)}
            color="muted-foreground"
            className="text-muted"
            size="large"
            round={false}
          />

          <FromToArrows />

          <Tag
            name={t(switchMethod.toLocaleLowerCase() as string)}
            color="primary"
            size="large"
            round={false}
          />
        </Row>

        <Column>
          <LabeledTextarea
            label={t('comments')}
            value={state.config.comment}
            placeholder={t('type-here-a-comment')!}
            onChange={({ target }) =>
              dispatch({ config: ({ config }) => ({ ...config, comment: target.value }) })
            }
          />

          {switchMethod === 'STANDARD' && (
            <LabeledInput
              label={t('number-of-installments')}
              value={state.config.installments ?? 1}
              type="number"
              min={1}
              max={20}
              onChange={({ target }) =>
                dispatch({
                  config: ({ config }) => ({
                    ...config,
                    installments: Number(target.value) || 1,
                  }),
                })
              }
            />
          )}

          <Row justifyContent="space-between">
            <Button variant="outlined" onClick={() => modalHandlers.turnOff()}>
              <CircleXIcon />
              {t('cancel')}
            </Button>

            <Button loading={changePaymentType.loading} onClick={executeChoice}>
              {t('change')}
              <HandCoinsIcon />
            </Button>
          </Row>
        </Column>
      </Modal>

      <Button
        variant="ghost"
        onClick={() => {
          dispatch({
            config: {
              installments: switchMethod === 'STANDARD' ? 1 : undefined,
              comment: '',
            },
          });

          modalHandlers.turnOn();
        }}
      >
        <RefreshIcon size={18} />
        {t('change')}
      </Button>
    </>
  );
}

export default ChangePayment;
